
import React from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CheckCircle, DollarSign } from 'lucide-react';

interface BookingActionButtonsProps {
  bookingId: number;
  currentBookingStatus: string;
  currentPaymentStatus: string;
  onUpdate: () => void;
}

const BookingActionButtons = ({ 
  bookingId, 
  currentBookingStatus, 
  currentPaymentStatus, 
  onUpdate 
}: BookingActionButtonsProps) => {
  const confirmBooking = async () => {
    try {
      console.log('Confirming booking with ID:', bookingId);
      
      const { data, error } = await supabase
        .from('New Bookings')
        .update({ booking_status: 'confirmed' })
        .eq('id', bookingId)
        .select();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Booking confirmed successfully:', data);
      toast.success('تم تأكيد الحجز بنجاح!');
      onUpdate();
    } catch (error) {
      console.error('Error confirming booking:', error);
      toast.error('فشل في تأكيد الحجز');
    }
  };

  const markAsPaid = async () => {
    try {
      console.log('Marking booking as paid with ID:', bookingId);
      
      const { data, error } = await supabase
        .from('New Bookings')
        .update({ 'Payment Status': 'paid' })
        .eq('id', bookingId)
        .select();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Payment status updated successfully:', data);
      toast.success('تم تحديث حالة الدفع بنجاح!');
      onUpdate();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('فشل في تحديث حالة الدفع');
    }
  };

  // Show completed status if both are done
  if (currentBookingStatus === 'confirmed' && currentPaymentStatus === 'paid') {
    return (
      <div className="flex gap-2">
        <div className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm font-medium border border-green-200">
          <CheckCircle className="h-3 w-3" />
          مؤكد
        </div>
        <div className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm font-medium border border-green-200">
          <DollarSign className="h-3 w-3" />
          مدفوع
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        {currentBookingStatus !== 'confirmed' && (
          <Button
            onClick={confirmBooking}
            size="sm"
            variant="outline"
            className="flex items-center gap-1 hover:bg-blue-50 hover:border-blue-300 border-blue-200 text-blue-700"
          >
            <CheckCircle className="h-3 w-3" />
            تأكيد
          </Button>
        )}
        
        {currentPaymentStatus !== 'paid' && (
          <Button
            onClick={markAsPaid}
            size="sm"
            variant="outline"
            className="flex items-center gap-1 hover:bg-green-50 hover:border-green-300 border-green-200 text-green-700"
          >
            <DollarSign className="h-3 w-3" />
            تحديد كمدفوع
          </Button>
        )}
      </div>
      
      {/* Status indicators */}
      <div className="flex gap-2">
        {currentBookingStatus === 'confirmed' && currentPaymentStatus !== 'paid' && (
          <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium border border-blue-200">
            <CheckCircle className="h-3 w-3" />
            مؤكد
          </div>
        )}
        
        {currentPaymentStatus === 'paid' && currentBookingStatus !== 'confirmed' && (
          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium border border-green-200">
            <DollarSign className="h-3 w-3" />
            مدفوع
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingActionButtons;
